import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    useTheme,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Divider,
    Menu
} from '@mui/material';
import {
    Inventory as StockIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    TrendingUp,
    TrendingDown,
    FilterList as FilterIcon,
    Search as SearchIcon,
    GetApp,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    X,
    PictureAsPdf,
    TableChart,
    Warning,
    AttachMoney,
    History as HistoryIcon,
    Adjust as AdjustIcon,
    CheckCircle,
    Error,
    Inventory,
    Description
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
    APIBarStockList,
    APIBarStockStats,
    APIBarStockUpdateBalance,
    APIBarStockHistory,
    APIBarStockTaking,
    APIUserNames,
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
    formatCreatedAtDate, 
    formatNumberWithComma, 
    formatNumberWithComma2, 
    getCategoriesNameById, 
    getStatusById, 
    getUserDetails, 
    recordsPerPage, 
    statusData 
} from '../../../utils/helpers';
import { 
    exportStockHistoryPDF,
    exportStocksToCSV, 
    exportStocksToExcel, 
    exportStocksToPDF,
    exportStockTakingPDF 
} from '../../../services/bar/StockAdjustmentService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import useCustomToast from '../../../hooks/useToast';
import { fetchBranchs, fetchCounters } from '../../../utils/namesListUtils';
import { apiService } from '../../../apiServices/ApiService';

const StockAdjustment = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();

    const [pageState, setPageState] = useState({
        pageData: [],
        stockStats: {
            total_stock_items: 0,
            total_quantity: 0,
            total_balance: 0,
            total_value: 0,
            total_selling_value: 0,
            active_stock: 0,
            out_of_stock: 0,
            low_stock: 0,
            normal_stock: 0,
            high_stock: 0,
            potential_profit: 0,
            most_valuable_item: 'N/A',
            most_valuable_value: 0
        },
        stockHistory: []
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedHistoryRecord, setSelectedHistoryRecord] = useState(null); // For history details
    const [searchTerm, setSearchTerm] = useState('');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [historyExportAnchorEl, setHistoryExportAnchorEl] = useState(null); // For history export menu

    // Stock adjustment form state
    const [adjustmentForm, setAdjustmentForm] = useState({
        stockId: '',
        newQuantity: '',
        actionType: '',
        reason: '',
        notes: ''
    });

    // Filter state
    const [filters, setFilters] = useState({
        stock_level: '',
        status: '',
        dateFrom: '',
        dateTo: ''
    });

    // Dialog state
    const [openAdjustment, setOpenAdjustment] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);
    const [openHistoryDetails, setOpenHistoryDetails] = useState(false); // For history details dialog
    const [openView, setOpenView] = useState(false);
    const [filterOptions, setFilterOptions] = useState(false);
    const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [referenceData, setReferenceData] = useState({
        userNames: [],
        branchNames: [],
    });

    // Permissions
    const [userRole, setUserRole] = useState('');
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);

    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

    useEffect(() => {
        fetchData();
        fetchReferenceData();
    }, []);

    const fetchReferenceData = async () => {
        try {
            // Fetch all reference data in parallel
            const [branchesResult, usersResult] = await Promise.all([
                fetchBranchs(),
                apiService.listNames(APIUserNames),
                fetchCounters()
            ]);

            setReferenceData({
                branchNames: branchesResult || [],
                userNames: usersResult.success ? usersResult.data : [],
            });

            // Show info if no data found
            if (branchesResult.length === 0) {
                showToast("Info", "No branches found", "info");
            }
        } catch (error) {
            showToast("Error", "Failed to load reference data", "error");
        }
    };

    const fetchData = async (branchId = null) => {
        setLoading(true);
        setError(null);

        try {
            let branchIdToUse = branchId;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            const params = {};
            if (branchIdToUse) {
                params.branch_id = branchIdToUse;
            }

            const [pageRes, statsRes] = await Promise.all([
                axios.get(APIBarStockList, { params }),
                axios.get(APIBarStockStats, { params })
            ]);

            const stocks = Array.isArray(pageRes.data.data) ? pageRes.data.data.reverse() : [];

            setPageState(prev => ({
                ...prev,
                pageData: stocks,
                stockStats: statsRes.data.status === 'success' ? statsRes.data.data : prev.stockStats
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchStockStats = async () => {
        setStatsLoading(true);
        try {
            let branchIdToUse = selectedBranch;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            const params = {};
            if (branchIdToUse) {
                params.branch_id = branchIdToUse;
            }

            const response = await axios.get(APIBarStockStats, { params });
            if (response.data.status === 'success') {
                setPageState(prev => ({
                    ...prev,
                    stockStats: response.data.data
                }));
            }
        } catch (error) {
            console.error("Error fetching stock stats:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    const handleBranchSelect = (branchId) => {
        setSelectedBranch(branchId);
        setBranchSelectionOpen(false);
        fetchData(branchId);
        fetchStockStats();
    };

    // Helper function to get stock level info
    const getStockLevelInfo = (balance) => {
        if (balance <= 0) {
            return { level: 'Out of Stock', color: theme.palette.error.main, bgColor: theme.palette.error.light };
        } else if (balance <= 10) {
            return { level: 'Low', color: theme.palette.warning.main, bgColor: theme.palette.warning.light };
        } else if (balance <= 50) {
            return { level: 'Normal', color: theme.palette.success.main, bgColor: theme.palette.success.light };
        } else {
            return { level: 'High', color: theme.palette.info.main, bgColor: theme.palette.info.light };
        }
    };

    // Filter stocks based on search term and filters
    const filteredData = pageState.pageData.filter(stock => {
        const matchesSearch = searchTerm === '' ||
            stock.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            stock.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            stock.uom.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStockLevel = !filters.stock_level || 
            getStockLevelInfo(stock.balance).level === filters.stock_level;

        const matchesStatus = !filters.status || 
            stock.status == filters.status;

        let matchesDate = true;
        if (filters.dateFrom) {
            const stockDate = new Date(stock.stock_date).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && stockDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const stockDate = new Date(stock.stock_date).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && stockDate <= filterToDate;
        }

        return matchesSearch  && matchesStockLevel && matchesStatus && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenAdjustment = (stock) => {
        setSelectedRecord(stock);
        setAdjustmentForm({
            stockId: stock.id,
            newQuantity: stock.balance.toString(),
            actionType: '',
            reason: '',
            notes: ''
        });
        setOpenAdjustment(true);
    };

    const handleCloseAdjustment = () => {
        setOpenAdjustment(false);
        setSelectedRecord(null);
        setAdjustmentForm({
            stockId: '',
            newQuantity: '',
            actionType: '',
            reason: '',
            notes: ''
        });
    };

    const handleOpenHistory = async (stock) => {
        setSelectedRecord(stock);
        setOpenHistory(true);
        await fetchStockHistory(stock.id);
    };

    const handleCloseHistory = () => {
        setOpenHistory(false);
        setSelectedRecord(null);
        setPageState(prev => ({
            ...prev,
            stockHistory: []
        }));
    };

    const handleOpenHistoryDetails = (historyRecord) => {
        setSelectedHistoryRecord(historyRecord);
        setOpenHistoryDetails(true);
    };

    const handleCloseHistoryDetails = () => {
        setOpenHistoryDetails(false);
        setSelectedHistoryRecord(null);
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            stock_level: '',
            status: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    // Export handlers
    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleHistoryExportClick = (event) => {
        setHistoryExportAnchorEl(event.currentTarget);
    };

    const handleHistoryExportClose = () => {
        setHistoryExportAnchorEl(null);
    };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            await exportStocksToExcel(filteredData, referenceData, filters);
            showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            await exportStocksToPDF(filteredData, referenceData.branchNames, filters);
            showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            await exportStocksToCSV(filteredData, referenceData.branchNames);
            showToast("Success", "CSV file downloaded successfully", "success");
        } catch (error) {
            console.error('CSV export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    // Handle stock taking PDF export
    const handleStockTakingPDF = async () => {
        try {
            handleExportClose();
            
            // Fetch current stock data
            const response = await axios.get(APIBarStockTaking, {
                params: {
                    branch_id: selectedBranch || getUserDetails("branchId")
                }
            });
            
            if (response.data.status === 'success') {
                await exportStockTakingPDF(
                    response.data.data,
                    referenceData.branchNames
                );
                showToast("Success", "Stock taking PDF downloaded successfully", "success");
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error('Stock taking PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    // Handle history PDF export - Updated to use frontend PDF generation
    const handleHistoryPDFExport = async () => {
        try {
            handleHistoryExportClose();
            
            if (!selectedRecord) {
                showToast("Error", "No stock item selected", "error");
                return;
            }

            if (pageState.stockHistory.length === 0) {
                showToast("Warning", "No history data to export", "warning");
                return;
            }

            // Generate PDF on frontend using existing stock history data
            await exportStockHistoryPDF(
                selectedRecord,
                pageState.stockHistory,
                referenceData.userNames
            );
            
            showToast("Success", "History PDF downloaded successfully", "success");
        } catch (error) {
            console.error('History PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    // Stock adjustment form handlers
    const handleAdjustmentInputChange = (e) => {
        const { name, value } = e.target;
        setAdjustmentForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle adjust stock form submission
    const handleAdjustStock = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!adjustmentForm.stockId) {
            showToast("Error", "Please select a stock item", "error");
            return;
        }
        
        if (!adjustmentForm.newQuantity || parseInt(adjustmentForm.newQuantity) < 0) {
            showToast("Error", "Please enter a valid quantity", "error");
            return;
        }

        try {
            const formData = new FormData();
            formData.append('stock_id', adjustmentForm.stockId);
            formData.append('new_quantity', adjustmentForm.newQuantity);
            formData.append('action_type', adjustmentForm.actionType);
            formData.append('reason', adjustmentForm.reason);
            formData.append('notes', adjustmentForm.notes);
            formData.append('user_id', getUserDetails('Id'));

            const response = await axios.post(APIBarStockUpdateBalance, formData);

            if (response.data.status === "success") {
                showToast("Success", response.data.message, "success");
                handleCloseAdjustment();
                fetchData(); // Refresh data
                fetchStockStats(); // Refresh stats
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast("Validation Error", errorMessages.join('\n') || "Please correct the form errors", "error");
                } else if (status === 409) {
                    showToast("Error", data.message || "Conflict occurred", "error");
                } else if (status >= 500) {
                    showToast("Server Error", data.message || "Internal server error", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else if (error.request) {
                showToast("Network Error", "No response from server. Please check your connection.", "error");
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    // Fetch stock history
    const fetchStockHistory = async (stockId) => {
        setHistoryLoading(true);
        try {
            const response = await axios.get(`${APIBarStockHistory}${stockId}`);
            if (response.data.status === "success") {
                setPageState(prev => ({
                    ...prev,
                    stockHistory: response.data.data.history || []
                }));
            } else {
                showToast("Warning", response.data.message || "Failed to fetch history", "warning");
            }
        } catch (error) {
            let errorMessage = "Failed to load stock history";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            showToast("Error", errorMessage, "error");
            setPageState(prev => ({
                ...prev,
                stockHistory: []
            }));
        } finally {
            setHistoryLoading(false);
        }
    };

    // Format action type for display
    const formatActionType = (action) => {
        const actions = {
            'adjustment': { label: 'Adjustment', color: 'primary', icon: <AdjustIcon /> },
            'reduce': { label: 'Reduce', color: 'warning', icon: <Warning /> },
        };
        return actions[action] || { label: action, color: 'default', icon: null };
    };

    const getSelectedBranchName = () => {
        if (!selectedBranch) return "Select Branch";
        const branch = referenceData.branchNames.find(b => b.id === selectedBranch);
        return branch ? `${branch.name} (${branch.code})` : "Select Branch";
    };

    // Get user name by ID
    const getUserNameById = (userId) => {
        if (!referenceData.userNames || !Array.isArray(referenceData.userNames)) return 'Unknown';
        const user = referenceData.userNames.find(user => user.id == userId);
        return user ? user.name : 'Unknown';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }
    
    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography variant="h6" sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                mb: 3,
                textAlign: 'center'
            }}>
                Bar Stock Management ( {formatNumberWithComma2(pageState.pageData.length)} Total Items )
            </Typography>
            
            {/* Display selected branch name */}
            {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
                <Box sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Chip
                        label={`Selected Branch: ${getSelectedBranchName()}`}
                        color="primary"
                        variant="filled"
                        onDelete={() => {
                            setSelectedBranch('');
                            fetchData();
                            fetchStockStats();
                        }}
                        deleteIcon={<CloseIcon />}
                    />
                </Box>
            )}

            {/* Stock Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Total Stock Items */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'primary.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <StockIcon sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Items
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                                        formatNumberWithComma(pageState.stockStats.total_stock_items || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                All stock items
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Current Balance */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'success.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CheckCircle sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Current Balance
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                                        formatNumberWithComma(pageState.stockStats.total_balance || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Total items available
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Value */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'warning.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <AttachMoney sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Value
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                                        formatNumberWithComma(pageState.stockStats.total_value || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                At cost price
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Low Stock Alert */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: pageState.stockStats.low_stock > 0 ? 'warning.dark' : 'success.dark',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Warning sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Low Stock Items
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                                        formatNumberWithComma(pageState.stockStats.low_stock || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Items with stock ≤ 10
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Out of Stock */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: pageState.stockStats.out_of_stock > 0 ? 'error.dark' : 'success.dark',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Error sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Out of Stock
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                                        formatNumberWithComma(pageState.stockStats.out_of_stock || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Items with 0 stock
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Potential Profit */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'info.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TrendingUp sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Potential Profit
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                                        formatNumberWithComma(pageState.stockStats.potential_profit || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                (Selling - Cost)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Buttons */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexShrink: 0,
                        order: { xs: 2, sm: 1 }
                    }}>
                        {hasPermission(PERMISSIONS.BRANCHES) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<FilterIcon />}
                                onClick={() => setBranchSelectionOpen(true)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Select Branch
                            </Button>
                        )}
                        
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={handleFilterOptions}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            View Options
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PictureAsPdf />}
                            onClick={handleStockTakingPDF}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            Stock Taking
                        </Button>

                        {hasPermission(PERMISSIONS.EXPORT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchData();
                                fetchStockStats();
                            }}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Search Bar */}
                    <Paper sx={{
                        width: { xs: '100%', sm: '300px' },
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: { xs: 0, sm: 'auto' },
                        order: { xs: 1, sm: 2 },
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search stock..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {( filters.stock_level || filters.status || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.stock_level && (
                            <Chip
                                label={`Stock Level: ${filters.stock_level}`}
                                onDelete={() => setFilters(prev => ({ ...prev, stock_level: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusById(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip 
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip 
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button 
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Stocks Table */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Stock Date</TableCell>
                                    <TableCell>Item Name</TableCell>
                                    <TableCell>Balance</TableCell>
                                    <TableCell>Cost</TableCell>
                                    <TableCell>Selling</TableCell>
                                    <TableCell>Stock Level</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((stock, index) => {
                                    const stockLevel = getStockLevelInfo(stock.balance);
                                    return (
                                        <TableRow
                                            key={stock.id || index}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {new Date(stock.stock_date).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                {stock.name}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatNumberWithComma2(stock.balance)}
                                                    color={stock.balance > 0 ? "success" : "error"}
                                                    variant="filled"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatNumberWithComma(stock.cost)}
                                                    color="success"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatNumberWithComma(stock.selling)}
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={stockLevel.level}
                                                    sx={{
                                                        backgroundColor: stockLevel.bgColor,
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {stock.status === 1 ? (
                                                    <Chip
                                                        label={getStatusById(stock.status)}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Chip
                                                        label={getStatusById(stock.status)}
                                                        size="small"
                                                        color="error"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenView(stock)}
                                                    color="primary"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.info.light
                                                        }
                                                    }}
                                                >
                                                    <SearchIcon />
                                                </IconButton>
                                                {hasPermission(PERMISSIONS.ADD) && (
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenAdjustment(stock)}
                                                        color="warning"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.warning.light
                                                            }
                                                        }}
                                                    >
                                                        <AdjustIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenHistory(stock)}
                                                    color="secondary"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.secondary.light
                                                        }
                                                    }}
                                                >
                                                    <HistoryIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <StockIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No stock items found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Branch Selection Dialog */}
            <Dialog
                open={branchSelectionOpen}
                onClose={() => setBranchSelectionOpen(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="branch-select-label">Select Branch</InputLabel>
                            <Select
                                labelId="branch-select-label"
                                value={selectedBranch}
                                label="Select Branch"
                                onChange={(e) => handleBranchSelect(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select a branch</em>
                                </MenuItem>
                                {referenceData.branchNames.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name} ({branch.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
                            <strong>Note:</strong> Selecting a branch will filter stock only from that branch. 
                            Leave unselected to view all stock.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setBranchSelectionOpen(false)}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (selectedBranch) {
                                handleBranchSelect(selectedBranch);
                            }
                            setBranchSelectionOpen(false);
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="info" />
                    Export to CSV (.csv)
                </MenuItem>
            </Menu>

            {/* History Export Menu */}
            <Menu
                anchorEl={historyExportAnchorEl}
                open={Boolean(historyExportAnchorEl)}
                onClose={handleHistoryExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleHistoryPDFExport} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export History to PDF
                </MenuItem>
            </Menu>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={handleCloseOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Stock
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Stock
                        </Button>

                        <Divider>OR</Divider>

                        <FormControl fullWidth>
                            <InputLabel>Stock Level</InputLabel>
                            <Select
                                name="stock_level"
                                value={filters.stock_level}
                                onChange={handleFilterChange}
                                label="Stock Level"
                            >
                                <MenuItem value="">All Levels</MenuItem>
                                <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                                <MenuItem value="Low">Low Stock</MenuItem>
                                <MenuItem value="Normal">Normal Stock</MenuItem>
                                <MenuItem value="High">High Stock</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusData.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Stock Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseOptions}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleCloseOptions}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Adjust Stock Dialog - FIXED */}
            <Dialog
                open={openAdjustment}
                onClose={handleCloseAdjustment}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <AdjustIcon sx={{ color: theme.palette.warning.main }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Adjust Stock Balance
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseAdjustment}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Update stock balance with tracking
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box component="form" onSubmit={handleAdjustStock} sx={{ mt: 2 }}>
                        {/* Current Stock Display */}
                        {selectedRecord && (
                            <Alert 
                                severity="info" 
                                sx={{ mb: 3 }}
                                icon={<StockIcon />}
                            >
                                <Typography variant="body2">
                                    <strong>Stock Item:</strong> {selectedRecord.name}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Current Balance:</strong> {formatNumberWithComma(selectedRecord.balance)} {selectedRecord.uom}
                                </Typography>
                            </Alert>
                        )}

                        {/* Action Type Selection */}
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Adjustment Type</InputLabel>
                            <Select
                                name="actionType"
                                value={adjustmentForm.actionType}
                                onChange={handleAdjustmentInputChange}
                                label="Adjustment Type"
                            >
                                <MenuItem >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography>Select Action Type</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="adjustment">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AdjustIcon sx={{ color: 'primary.main' }} />
                                        <Typography>Stock Adjustment</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="reduce">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Warning sx={{ color: 'warning.main' }} />
                                        <Typography>Reduce Stock</Typography>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {/* New Quantity Input */}
                        <TextField
                            fullWidth
                            label="New Stock Balance"
                            name="newQuantity"
                            type="number"
                            value={adjustmentForm.newQuantity}
                            onChange={handleAdjustmentInputChange}
                            required
                            inputProps={{ min: 0 }}
                            placeholder="Enter new stock balance"
                            helperText="Enter the actual counted quantity"
                            sx={{ mb: 3 }}
                        />

                        {/* Reason Input */}
                        <TextField
                            fullWidth
                            label="Reason for Adjustment"
                            name="reason"
                            value={adjustmentForm.reason}
                            onChange={handleAdjustmentInputChange}
                            required
                            placeholder="e.g., Physical count, Damaged items, Found items"
                            sx={{ mb: 2 }}
                        />

                        {/* Notes Input */}
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Additional Notes (Optional)"
                            name="notes"
                            value={adjustmentForm.notes}
                            onChange={handleAdjustmentInputChange}
                            placeholder="Any additional information about this adjustment..."
                            sx={{ mb: 3 }}
                        />

                        {/* Preview Section */}
                        {adjustmentForm.newQuantity && selectedRecord && (
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 2 }}>
                                        Adjustment Preview
                                    </Typography>
                                    <Box sx={{ spaceY: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Current Balance:</Typography>
                                            <Typography variant="body2">
                                                {formatNumberWithComma(selectedRecord.balance)} {selectedRecord.uom}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">New Balance:</Typography>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: parseInt(adjustmentForm.newQuantity) > selectedRecord.balance ? 'success.main' : 'error.main',
                                                    fontWeight: 'medium'
                                                }}
                                            >
                                                {formatNumberWithComma(parseInt(adjustmentForm.newQuantity) || 0)} {selectedRecord.uom}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Change:</Typography>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: parseInt(adjustmentForm.newQuantity) > selectedRecord.balance ? 'success.main' : 'error.main',
                                                    fontWeight: 'medium'
                                                }}
                                            >
                                                {parseInt(adjustmentForm.newQuantity) > selectedRecord.balance ? '+' : ''}
                                                {parseInt(adjustmentForm.newQuantity) - selectedRecord.balance} {selectedRecord.uom}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                        <input type="hidden" name="stockId" value={adjustmentForm.stockId} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAdjustment} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAdjustStock}
                        variant="contained"
                        color="warning"
                        startIcon={<AdjustIcon />}
                    >
                        Update Stock Balance
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog - FIXED */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Stock Item Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Item Name:</b> {selectedRecord.name}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>UOM:</b> {selectedRecord.uom}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Initial Quantity:</b> {formatNumberWithComma(selectedRecord.quantity)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Current Balance:</b> {formatNumberWithComma(selectedRecord.balance)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Cost Price:</b> {formatNumberWithComma(selectedRecord.cost)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Selling Price:</b> {formatNumberWithComma(selectedRecord.selling)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Total Cost:</b> {formatNumberWithComma(selectedRecord.total_cost)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Total Selling:</b> {formatNumberWithComma(selectedRecord.total_selling)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Stock Date:</b> {new Date(selectedRecord.stock_date).toLocaleDateString()}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Stock Level:</b>
                                        <Chip
                                            label={getStockLevelInfo(selectedRecord.balance).level}
                                            sx={{
                                                ml: 1,
                                                backgroundColor: getStockLevelInfo(selectedRecord.balance).bgColor,
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                            size="small"
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Status:</b>
                                        {selectedRecord.status === 1 ? (
                                            <Chip
                                                label={getStatusById(selectedRecord.status)}
                                                size="small"
                                                color="primary"
                                                sx={{ ml: 1 }}
                                            />
                                        ) : (
                                            <Chip
                                                label={getStatusById(selectedRecord.status)}
                                                size="small"
                                                color="error"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </Typography>
                                </Grid>
                                {hasPermission(PERMISSIONS.BRANCHES) && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2"><b>Branch:</b>
                                            <Chip
                                                label={getCategoriesNameById(selectedRecord.branch_id, referenceData.branchNames) || 'N/A'}
                                                color="info"
                                                variant="outlined"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                            <Box sx={{ mt: 3 }}>
                                {selectedRecord.description && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2"><b>Description:</b> {selectedRecord.description}</Typography>
                                    </Grid>
                                )}
                                {selectedRecord.notes && (
                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Dialog with Export Button */}
            <Dialog
                open={openHistory}
                onClose={handleCloseHistory}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="lg"
                sx={{ '& .MuiDialog-paper': { maxHeight: '80vh' } }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <HistoryIcon />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Stock History - {selectedRecord?.name}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {pageState.stockHistory.length > 0 && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<PictureAsPdf />}
                                    onClick={handleHistoryExportClick}
                                    size="small"
                                >
                                    Export PDF
                                </Button>
                            )}
                            <IconButton onClick={handleCloseHistory}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Current Balance: {selectedRecord ? formatNumberWithComma(selectedRecord.balance || 0) : 0} {selectedRecord?.uom}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {historyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : pageState.stockHistory.length > 0 ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            '& th': {
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem'
                                            }
                                        }}
                                    >
                                        <TableCell>Date</TableCell>
                                        <TableCell>Action</TableCell>
                                        <TableCell align="right">Old Balance</TableCell>
                                        <TableCell align="right">Change</TableCell>
                                        <TableCell align="right">New Balance</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell align="center">Details</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pageState.stockHistory.map((history, index) => {
                                        const actionInfo = formatActionType(history.action_type);
                                        return (
                                            <TableRow key={history.id || index} hover>
                                                <TableCell>
                                                    {formatCreatedAtDate(history.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={actionInfo.label}
                                                        color={actionInfo.color}
                                                        size="small"
                                                        icon={actionInfo.icon}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumberWithComma(history.old_quantity)} {selectedRecord?.uom}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: history.quantity_change >= 0 ? 'success.main' : 'error.main',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {history.quantity_change >= 0 ? '+' : ''}
                                                        {formatNumberWithComma(history.quantity_change)} {selectedRecord?.uom}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumberWithComma(history.new_quantity)} {selectedRecord?.uom}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {getUserNameById(history.user_id)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenHistoryDetails(history)}
                                                        title="View Details"
                                                    >
                                                        <Description />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No stock history found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No adjustments have been recorded for this stock item yet.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHistory} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Details Dialog */}
            <Dialog
                open={openHistoryDetails}
                onClose={handleCloseHistoryDetails}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Adjustment Details
                        </Typography>
                        <IconButton onClick={handleCloseHistoryDetails}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedHistoryRecord && (
                        <Box sx={{ p: 2 }}>
                            <Box>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Action Information
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        {(() => {
                                            const actionInfo = formatActionType(selectedHistoryRecord.action_type);
                                            return (
                                                <Chip
                                                    label={actionInfo.label}
                                                    color={actionInfo.color}
                                                    icon={actionInfo.icon}
                                                />
                                            );
                                        })()}
                                        <Typography variant="body2" color="text.secondary">
                                            Date: {formatCreatedAtDate(selectedHistoryRecord.created_at)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        Old Balance:
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatNumberWithComma(selectedHistoryRecord.old_quantity)} {selectedRecord?.uom}
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        New Balance:
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: selectedHistoryRecord.new_quantity > selectedHistoryRecord.old_quantity ? 'success.main' : 'error.main',
                                        fontWeight: 'bold'
                                    }}>
                                        {formatNumberWithComma(selectedHistoryRecord.new_quantity)} {selectedRecord?.uom}
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        Change:
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: selectedHistoryRecord.quantity_change >= 0 ? 'success.main' : 'error.main',
                                        fontWeight: 'bold'
                                    }}>
                                        {selectedHistoryRecord.quantity_change >= 0 ? '+' : ''}
                                        {formatNumberWithComma(selectedHistoryRecord.quantity_change)} {selectedRecord?.uom}
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        User:
                                    </Typography>
                                    <Typography variant="body1">
                                        {getUserNameById(selectedHistoryRecord.user_id) || 'N/A'}
                                    </Typography>
                                </Grid>
                                <br/>
                                {selectedHistoryRecord.reason && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                            Reason:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                                            <Typography variant="body2">
                                                {selectedHistoryRecord.reason}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                <br/>
                                {selectedHistoryRecord.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                            Notes:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                                            <Typography variant="body2">
                                                {selectedHistoryRecord.notes}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHistoryDetails} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default StockAdjustment;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

